/** Adapter for wallet transactions (read-only). The API includes the wallet's
 *  client so we can show a client name. Maps to the UI `WalletTopup` for the
 *  "recent top-ups" list, and to a richer view for per-wallet history. */

import { DEFAULT_EXCHANGE_RATE } from "@/lib/constants";
import type { CardSource, WalletTopup } from "@/types";

export type WalletTxnType = "TOPUP" | "SPEND" | "ADJUSTMENT";

export interface ApiWalletTransaction {
  id: string;
  walletId: string;
  type: WalletTxnType;
  amountUsd: number | string;
  amountDzd: number | string;
  exchangeRate?: number | string | null;
  source?: CardSource | null;
  occurredAt?: string | null;
  createdAt?: string | null;
  note?: string | null;
  wallet?: { id?: string; clientId?: string; client?: { id?: string; name?: string | null } | null } | null;
}

export interface WalletTransactionView {
  id: string;
  walletId: string;
  clientName: string;
  type: WalletTxnType;
  amountUsd: number;
  amountDzd: number;
  rate: number;
  source?: CardSource;
  at: string;
  note?: string;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function formatDateTime(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** For the WalletsPage "Derniers rechargements" list. */
export function toWalletTopup(api: ApiWalletTransaction): WalletTopup {
  return {
    id: api.id,
    clientName: api.wallet?.client?.name ?? "—",
    amountUsd: toNumber(api.amountUsd),
    amountDzd: toNumber(api.amountDzd),
    at: formatDateTime(api.occurredAt ?? api.createdAt),
  };
}

/** For per-wallet transaction history (read-only). */
export function toWalletTransactionView(api: ApiWalletTransaction): WalletTransactionView {
  return {
    id: api.id,
    walletId: api.walletId,
    clientName: api.wallet?.client?.name ?? "—",
    type: api.type,
    amountUsd: toNumber(api.amountUsd),
    amountDzd: toNumber(api.amountDzd),
    rate: toNumber(api.exchangeRate) || DEFAULT_EXCHANGE_RATE,
    source: api.source ?? undefined,
    at: formatDateTime(api.occurredAt ?? api.createdAt),
    note: api.note ?? undefined,
  };
}
